import React, { useCallback, useMemo, useRef } from 'react';
import { BaseMenuItem, MenuItem, NavMenuDropdown } from 'hfs-common-ui';
import MenuConstants from '~/constants/menu-constants';
import { useDetectOutsideClick } from '~/hooks/use-detect-outside-click';
import { useGetMeasurementSystemPreferenceEnabled } from '~/config/firebase/remote-config';
import { useUptime } from '~/domain/uptime/hooks/use-uptime';
import UserPreferencesSubmenu from '~/components/containers/MenubarContainer/components/UserPreferencesSubmenu/UserPreferencesSubmenu';
import type { MappedMenuItem } from '../../types/types';
import { isIconMenu } from '../../utils';
import NavIcon from '../NavIcon/NavIcon';
import UserProfileMenuHeader from '../UserProfileMenuHeader/UserProfileMenuHeader';
import * as S from './styled';

export interface Props {
  menuItem: MenuItem;
  activeId?: number;
  onClick: (menuItem: BaseMenuItem) => void;
}

const DropdownNavMenu = ({ menuItem, activeId, onClick }: Props) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { label, menuItems } = menuItem;
  const { isUptime } = useUptime();
  const hasSubMenu = menuItems && menuItems?.length > 0;
  const id = (menuItem.metadata as MappedMenuItem).id;
  const [isSubMenuOpen, setIsSubMenuOpen] = useDetectOutsideClick([dropdownRef], false);

  const isSelected = useMemo<boolean>(() => {
    if (activeId === id) {
      return true;
    }
    if (hasSubMenu) {
      return menuItems.some((item) => item.id === activeId);
    }
    return false;
  }, [id, activeId, hasSubMenu, menuItems]);

  const handleOnClick = useCallback(() => {
    setIsSubMenuOpen(true);
  }, [setIsSubMenuOpen]);

  const handleSubMenuOnClick = useCallback(
    (submenuItem: BaseMenuItem) => {
      setIsSubMenuOpen(false);
      onClick(submenuItem);
    },
    [onClick, setIsSubMenuOpen]
  );

  const isUserMenu = useMemo(() => {
    return menuItem.metadata.key === MenuConstants.USER;
  }, [menuItem.metadata.key]);

  const renderNavIcon = useMemo(() => {
    if (isIconMenu(menuItem.metadata)) {
      return (
        <NavIcon
          icon={menuItem.metadata.icon}
          activeIcon={menuItem.metadata.activeIcon}
          isActive={isSubMenuOpen || isSelected}
          id={
            menuItem.metadata.key ? menuItem.metadata.key.toLowerCase() + 'DropdownLink' : undefined
          }
        />
      );
    }
  }, [isSelected, isSubMenuOpen, menuItem.metadata]);

  const renderIconsAndLabels = useCallback(() => {
    if (isUserMenu) {
      return <UserProfileMenuHeader onlyAvatar />;
    } else if (menuItem.metadata.icon) {
      return renderNavIcon;
    } else {
      return label;
    }
  }, [isUserMenu, label, menuItem.metadata.icon, renderNavIcon]);

  const { data: measurementSystemSelectionEnabled } = useGetMeasurementSystemPreferenceEnabled();

  return (
    <S.DropdownLink
      id={id}
      data-testid="rootMenuContainer"
      $isSubMenuOpen={isSubMenuOpen}
      $isUserMenu={isUserMenu}
      onClick={handleOnClick}
    >
      {renderIconsAndLabels()}
      {hasSubMenu && (
        <S.DropdownMenuContent ref={dropdownRef} data-testid="submenuContainer">
          <S.NavMenuWrapper>
            {isUserMenu && measurementSystemSelectionEnabled && !isUptime ? (
              <UserPreferencesSubmenu onSubmenuClick={handleSubMenuOnClick} menuItems={menuItems} />
            ) : (
              <NavMenuDropdown
                menu={menuItems}
                activeMenuItemId={activeId}
                onClick={handleSubMenuOnClick}
              />
            )}
          </S.NavMenuWrapper>
        </S.DropdownMenuContent>
      )}
    </S.DropdownLink>
  );
};

export default DropdownNavMenu;
